import { UiIcon } from './UiIcon'
import { GameScreen } from './GameScreen'
import { HomeScreen } from './HomeScreen'
import { PanelDialog } from './PanelDialog'
import type { PanelDialogProps } from './view-types'
import type { Panel } from '../view-types'
import type { GameState } from '../game'

type AppShellProps = {
  screen: 'home' | 'game'
  save: GameState | null
  ready: boolean
  saveStatus: 'saved' | 'memory'
  locationLabel: string
  reduceMotion: boolean
  soundEnabled: boolean
  restartMemories: GameState['history'] | null
  birthIntroToken: number | null
  onBirthIntroComplete: () => void
  panel: Panel
  dialogProps: Omit<PanelDialogProps, 'panel' | 'state'>
  onPanel: (panel: Panel) => void
  onToggleSound: () => void
  onStart: () => void
  onContinue: () => void
  onViewEnding: () => void
  onHome: () => void
  onChange: (state: GameState) => void
}

/** The visual chrome and page switcher. It owns no save, auth, or cloud state. */
export function AppShell({ screen, save, ready, saveStatus, locationLabel, reduceMotion, soundEnabled, restartMemories, birthIntroToken, onBirthIntroComplete, panel, dialogProps, onPanel, onToggleSound, onStart, onContinue, onViewEnding, onHome, onChange }: AppShellProps) {
  const gameActive = screen === 'game' && Boolean(save)
  const appClass = `app-shell ${gameActive ? 'game-active' : 'home-active'}${reduceMotion ? ' reduce-motion' : ''}`

  return (
    <div className={appClass}>
      <header className="site-header">
        {!gameActive && <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true"><UiIcon name="✦" /></span>
          <div>
            <p className="eyebrow">SUN GE · LIFE BOOK</p>
            <h1>孙割人生模拟器</h1>
          </div>
        </div>}
        <div className="header-actions">
          <button className="icon-button sound-toggle" data-enabled={soundEnabled} onClick={onToggleSound} aria-label={soundEnabled ? '关闭背景音乐与音效' : '开启背景音乐与音效'} title={soundEnabled ? '关闭背景音乐与音效' : '开启背景音乐与音效'}><UiIcon name={soundEnabled ? "sound" : "muted"} /></button>
          {!gameActive && <button className="icon-button" onClick={() => onPanel('settings')} aria-label="打开设置"><UiIcon name="⚙" /></button>}
        </div>
      </header>

      <main className="page-wrap">
        {screen === 'home' || !save ? (
          <HomeScreen save={save} ready={ready} onStart={onStart} onContinue={onContinue} onViewEnding={onViewEnding} onPanel={onPanel} />
        ) : (
          <GameScreen state={save} reduceMotion={reduceMotion} soundEnabled={soundEnabled} birthIntroToken={birthIntroToken} restartMemories={restartMemories} onBirthIntroComplete={onBirthIntroComplete} onStartNewRun={onStart} onHome={onHome} onPanel={onPanel} onChange={onChange} />
        )}
      </main>

      <footer className={`site-footer${gameActive ? ' game-footer' : ''}`}>
        <span>{locationLabel.split(' · ')[0]}</span>
        <span className="save-dot" data-state={saveStatus} aria-label={saveStatus === 'saved' ? '已保存' : '当前仅临时保存'} />
        <span>{saveStatus === 'saved' ? '已保存' : '临时保存'}</span>
        <span aria-hidden="true">·</span>
        <a className="site-footer-link" href="/privacy-policy.html">隐私政策</a>
      </footer>

      {panel && <PanelDialog panel={panel} state={save} {...dialogProps} />}
    </div>
  )
}
